import { useState } from 'react';
import type { Voucher, VoucherLiabilityReport, VoucherStatus } from '@salonomy/types';
import { Button, Card, Field, Modal, Pill, SegmentedControl, Stat, TextArea } from '@salonomy/ui';
import {
  useExpireVouchers,
  useVoidVoucher,
  useVoucherLiabilityReport,
  useVouchers,
  VOUCHERS_PAGE,
} from '../../data/vouchers.js';
import { ApiClientError } from '../../lib/api.js';
import { money, moneyWhole } from '../../lib/format.js';
import { useToast } from '../../lib/toast.js';

/**
 * WS9 §2 — vouchers & gift cards admin: the issued list with the SERVER's derived balances, a per-row
 * Void (reason required), the liability reconciliation card, and the breakage sweep.
 *
 * HONESTY (ADR-0009, load-bearing): a sold gift card is a LIABILITY, never revenue. The only "revenue"
 * surfaced is the liability report's `revenueRecognisedPence` (recognised at redemption) — NEVER the sold
 * total. Balances are the server's `balancePence`; nothing is recomputed here. The `reconciles` boolean is
 * surfaced prominently — a clear warning if the ledger doesn't net to the penny.
 *
 * Void + breakage sweep MOVE money (idempotent server-side): each mints an idempotency key once, confirms,
 * and gates on isPending so a double-tap is a safe no-op. Manager-gated: a non-manager sees an honest note,
 * the liability read never fires (it's manager-only on the API), and no mutation is offered.
 */

const STATUS_TABS = [
  { value: 'active' as const, label: 'Active' },
  { value: 'expired' as const, label: 'Expired' },
  { value: 'void' as const, label: 'Void' },
];

const STATUS_TONE: Record<VoucherStatus, 'success' | 'neutral' | 'danger'> = {
  active: 'success',
  expired: 'neutral',
  void: 'danger',
};

export function VouchersAdminPanel({ isFloorManager }: { isFloorManager: boolean }) {
  if (!isFloorManager) {
    return (
      <Card>
        <div className="section-title">Vouchers &amp; gift cards</div>
        <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
          The gift-card liability report, voiding and the breakage sweep are available to salon
          owners and managers. Issuing and redeeming vouchers happens at the till.
        </div>
      </Card>
    );
  }
  return <VouchersAdminBody />;
}

function VouchersAdminBody() {
  const [status, setStatus] = useState<VoucherStatus>('active');
  const [offset, setOffset] = useState(0);
  const [voiding, setVoiding] = useState<Voucher | null>(null);

  const { data, isLoading, isError } = useVouchers(status, offset);
  const vouchers = data?.data ?? [];
  const total = data?.pagination.total ?? 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <LiabilityCard />

      <Card>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            marginBottom: 12,
          }}
        >
          <div className="section-title" style={{ margin: 0 }}>
            Issued vouchers
          </div>
          <SegmentedControl<VoucherStatus>
            options={STATUS_TABS}
            value={status}
            onChange={(v) => {
              setStatus(v);
              setOffset(0);
            }}
          />
        </div>

        {isError ? (
          <div className="muted" style={{ fontSize: 13 }}>
            Couldn’t load vouchers.
          </div>
        ) : isLoading ? (
          <div className="muted" style={{ fontSize: 13 }}>
            Loading vouchers…
          </div>
        ) : vouchers.length === 0 ? (
          <div className="muted" style={{ fontSize: 13 }}>
            No {status} vouchers.
          </div>
        ) : (
          <>
            <div className="settings-list">
              {vouchers.map((v) => (
                <div key={v.id} className="settings-row">
                  <div>
                    <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className="mono">{v.code}</span>
                      <Pill tone={STATUS_TONE[v.status]}>{v.status}</Pill>
                      <Pill tone="soft">{v.kind === 'gift_card' ? 'Gift card' : 'Voucher'}</Pill>
                    </div>
                    <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>
                      Issued {money(v.originalAmountPence)} · expires{' '}
                      {new Date(v.expiresAt).toLocaleDateString('en-GB')}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {/* The SERVER's derived balance — never recomputed here. */}
                    <Stat label="Balance" value={money(v.balancePence)} />
                    {v.status === 'active' ? (
                      <Button variant="outline" size="sm" onClick={() => setVoiding(v)}>
                        Void
                      </Button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>

            {total > VOUCHERS_PAGE ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: 12,
                }}
              >
                <span className="muted" style={{ fontSize: 12.5 }}>
                  {offset + 1}–{Math.min(offset + VOUCHERS_PAGE, total)} of {total}
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={offset === 0}
                    onClick={() => setOffset((o) => Math.max(0, o - VOUCHERS_PAGE))}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={offset + VOUCHERS_PAGE >= total}
                    onClick={() => setOffset((o) => o + VOUCHERS_PAGE)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            ) : null}
          </>
        )}
      </Card>

      {voiding ? <VoidVoucherModal voucher={voiding} onClose={() => setVoiding(null)} /> : null}
    </div>
  );
}

/**
 * The gift-card liability reconciliation card (manager-only API). Surfaces sold / redeemed / expired /
 * outstanding, the SERVER's recognised revenue (NOT the sold total), and PROMINENTLY the `reconciles`
 * invariant — a clear warning if the ledger doesn't net to the penny. Includes the breakage sweep action.
 */
function LiabilityCard() {
  const { data: report, isLoading, isError, refetch } = useVoucherLiabilityReport(true);

  return (
    <Card>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          marginBottom: 4,
        }}
      >
        <div className="section-title" style={{ margin: 0 }}>
          Gift-card liability
        </div>
        <BreakageSweepButton onSwept={() => void refetch()} />
      </div>
      <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
        A sold gift card is a liability, not revenue. Revenue is recognised only when a voucher is
        redeemed — the figures below are the server’s, reconciled against the ledger.
      </div>

      {isError ? (
        <div className="muted" style={{ fontSize: 13 }}>
          Couldn’t load the liability report.
        </div>
      ) : isLoading || !report ? (
        <div className="muted" style={{ fontSize: 13 }}>
          Loading liability report…
        </div>
      ) : (
        <LiabilityBody report={report} />
      )}
    </Card>
  );
}

function LiabilityBody({ report }: { report: VoucherLiabilityReport }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* PROMINENT reconciliation status — a false reconciles is a money-integrity alarm. */}
      {report.reconciles ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 12px',
            borderRadius: 8,
            background: 'var(--slm-success-bg, #ecfdf3)',
            fontSize: 13,
          }}
        >
          <Pill tone="success">Reconciles</Pill>
          <span className="muted">
            Sold − redeemed − expired equals the outstanding liability, to the penny.
          </span>
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 12px',
            borderRadius: 8,
            background: 'var(--slm-danger-bg, #fef3f2)',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          <Pill tone="danger">Does not reconcile</Pill>
          <span>
            The liability ledger does not net to the penny. Do not rely on these figures — raise
            this with support before reporting.
          </span>
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 16,
        }}
      >
        <Stat label="Sold (liability)" value={moneyWhole(report.soldPence)} />
        <Stat label="Redeemed" value={moneyWhole(report.redeemedPence)} />
        <Stat label="Expired / breakage" value={moneyWhole(report.expiredPence)} />
        <Stat label="Outstanding liability" value={moneyWhole(report.outstandingPence)} />
      </div>

      <div className="settings-list">
        <div className="settings-row">
          <div>
            <div style={{ fontWeight: 600 }}>Recognised revenue</div>
            <div className="muted" style={{ fontSize: 12.5 }}>
              Revenue recognised at redemption (ex-VAT) — not the sold total.
            </div>
          </div>
          <Stat label="Recognised" value={money(report.revenueRecognisedPence)} />
        </div>
        <div className="settings-row">
          <div>
            <div style={{ fontWeight: 600 }}>Output VAT</div>
            <div className="muted" style={{ fontSize: 12.5 }}>
              VAT due on the recognised redemption revenue.
            </div>
          </div>
          <Stat label="VAT" value={money(report.vatOutputPence)} />
        </div>
        <div className="settings-row">
          <div>
            <div style={{ fontWeight: 600 }}>Active vouchers</div>
            <div className="muted" style={{ fontSize: 12.5 }}>
              Outstanding instruments still redeemable.
            </div>
          </div>
          <Pill tone="neutral">{report.activeVoucherCount}</Pill>
        </div>
      </div>
    </div>
  );
}

/**
 * The salon-scoped breakage sweep: expire every still-active voucher past its expiry, recognising
 * breakage_income. Confirm-gated, isPending-gated, and safe to re-run (an already-expired voucher is no
 * longer selected). The mutation takes no body; the server figures `{ expiredCount, breakagePence }` are
 * shown as-is — never recomputed.
 */
function BreakageSweepButton({ onSwept }: { onSwept: () => void }) {
  const toast = useToast();
  const expire = useExpireVouchers();
  const [confirming, setConfirming] = useState(false);

  async function run() {
    try {
      const result = await expire.mutateAsync();
      toast.success(
        result.expiredCount === 0
          ? 'No vouchers were past expiry — nothing to sweep.'
          : `Swept ${result.expiredCount} voucher${result.expiredCount === 1 ? '' : 's'} · ${money(
              result.breakagePence,
            )} breakage recognised`,
      );
      setConfirming(false);
      onSwept();
    } catch (err) {
      toast.error(
        err instanceof ApiClientError ? err.message : 'Could not run the breakage sweep.',
      );
    }
  }

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setConfirming(true)}>
        Run breakage sweep
      </Button>
      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        title="Run breakage sweep"
        width={440}
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button onClick={run} disabled={expire.isPending}>
              {expire.isPending ? 'Sweeping…' : 'Run sweep'}
            </Button>
          </>
        }
      >
        <div className="muted" style={{ fontSize: 13, lineHeight: 1.55 }}>
          This expires every still-active voucher whose expiry date has passed, writing a balanced
          breakage entry (the remaining balance becomes recognised breakage income) for each. It’s
          safe to re-run — a voucher already expired isn’t swept again. The exact breakage figure is
          calculated and returned by the server.
        </div>
      </Modal>
    </>
  );
}

/**
 * Void a single voucher — clears its remaining liability to comp_expense. A reason is REQUIRED (no silent
 * write); the idempotency key is minted once when the modal opens, the submit is isPending-gated, and the
 * action is confirmed by the modal itself.
 */
function VoidVoucherModal({ voucher, onClose }: { voucher: Voucher; onClose: () => void }) {
  const toast = useToast();
  const voidVoucher = useVoidVoucher();
  const [reason, setReason] = useState('');
  // Mint the idempotency key ONCE per intent (per opened modal); a re-submit replays the same key.
  const [idempotencyKey] = useState(() => crypto.randomUUID());

  const canVoid = reason.trim().length > 0;

  async function submit() {
    if (!canVoid) return;
    try {
      await voidVoucher.mutateAsync({ id: voucher.id, reason: reason.trim(), idempotencyKey });
      toast.success('Voucher voided');
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not void this voucher.');
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Void voucher"
      width={440}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" onClick={submit} disabled={!canVoid || voidVoucher.isPending}>
            {voidVoucher.isPending ? 'Voiding…' : 'Void voucher'}
          </Button>
        </>
      }
    >
      <div className="settings-list" style={{ marginBottom: 12 }}>
        <div className="settings-row">
          <span className="mono">{voucher.code}</span>
          <Stat label="Balance" value={money(voucher.balancePence)} />
        </div>
      </div>
      <div className="muted" style={{ fontSize: 13, marginBottom: 12, lineHeight: 1.5 }}>
        Voiding clears the remaining balance to comp expense — the holder can no longer redeem it.
        This is recorded against you with the reason below.
      </div>
      <Field label="Reason" hint="Required — voids are never silent.">
        <TextArea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why this voucher is being voided"
          rows={2}
        />
      </Field>
    </Modal>
  );
}
